import type { AuthUser } from '@/services/authServices'
import type { IUserPermission, IUserRole } from '@/type'

/**
 * Utility function to format dates
 * @param dateString - ISO date string
 * @param locale - Locale string (default: 'de-DE')
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  dateString: string,
  locale: string = 'de-DE',
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dateString) return '-'

  const date = new Date(dateString)

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }

  return date.toLocaleDateString(locale, options || defaultOptions)
}

/**
 * Utility function to format dates with time
 * @param dateString - ISO date string
 * @param locale - Locale string (default: 'de-DE')
 * @returns Formatted date string with time
 */
export function formatDateTime(dateString: string, locale: string = 'de-DE'): string {
  if (!dateString) return '-'

  const date = new Date(dateString)

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Utility function to format dates in long format
 * @param dateString - ISO date string
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted date string in long format
 */
export function formatDateLong(dateString: string, locale: string = 'en-US'): string {
  if (!dateString) return '-'

  const date = new Date(dateString)

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Narrow a date for PrimeVue's DatePicker, which only accepts `Date` objects,
 * while the API-facing types carry their dates as `string | Date`.
 * @param value - A Date, an API date string, or nothing
 * @returns The value as a Date, or null when it is empty or unparseable
 */
export function toDatePickerValue(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  const dateOnly = /^([0-9]{4})-([0-9]{2})-([0-9]{2})/.exec(value)
  if (dateOnly) {
    const [, year, month, day] = dateOnly
    return new Date(Number(year), Number(month) - 1, Number(day))
  }
  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Check if a user has at least one of the required permissions.
 *
 * Permissions are collected from the user's roles:
 *   user.roles[].permissions[].slug
 *
 * @param user - Authenticated user object from authStore
 * @param required - Single slug or array of slugs
 * @returns true if the user has at least one of the required permissions
 */
export function hasPermission(
  user: AuthUser | null | undefined,
  required: string | string[] | null | undefined,
): boolean {
  if (!required) return true

  let userPermissions: IUserPermission[] = []

  if (user?.roles) {
    // Support both a single role object and an array of roles
    const roles: IUserRole[] = Array.isArray(user.roles)
      ? (user.roles as unknown as IUserRole[])
      : [user.roles as unknown as IUserRole]

    roles.forEach((role) => {
      if (role.permissions && role.permissions.length) {
        userPermissions = userPermissions.concat(role.permissions)
      }
    })
  }

  const requiredPermissions = Array.isArray(required) ? required : [required]

  // User must have at least one of the required permissions
  return userPermissions.some((p) => requiredPermissions.includes(p.slug))
}
